import { useState, useEffect } from 'react';
import { MapPin, Star, ChevronRight, Heart, MessageCircle, RefreshCw, Tag, Calendar, Users, ChevronDown, Sparkles, Lock, X as XIcon, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useFoodlist } from '../contexts/FoodlistContext';
import { useActivityTracker } from '../hooks/useActivityTracker';
import LocationPickerModal from '../components/LocationPickerModal';
import SurpriseMeModal from '../components/SurpriseMeModal';
import UberStyleLocationPicker from '../components/UberStyleLocationPicker';
import StickyRightNav from '../components/StickyRightNav';
import QuestCard from '../components/QuestCard';
import EngagementBarCompact from '../components/EngagementBarCompact';
import AuthModal from '../components/AuthModal';
import UnifiedFoodCardModal from '../components/UnifiedFoodCardModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_IMG = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=75';

const SectionSkeleton = ({ count = 3, height = 'h-56' }) => (
  <div className={`grid grid-cols-1 md:grid-cols-${count === 2 ? '2' : '3'} gap-6`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className={`${height} bg-gray-200 rounded-2xl animate-pulse`} />
    ))}
  </div>
);

const Home = ({ savedItems, toggleSave }) => {
  const navigate = useNavigate();
  const { locationString, location: userLocation, setManualLocation, setGeocodedLocation } = useLocation();
  const { user } = useAuth();
  const { items: foodlistItems } = useFoodlist();
  const { track } = useActivityTracker();

  const [selectedCuisine, setSelectedCuisine] = useState('all');
  const [selectedVibe, setSelectedVibe] = useState('all');
  const [selectedDietary, setSelectedDietary] = useState('all');
  const [searchType, setSearchType] = useState('dine-out');
  const [dailyHighlight, setDailyHighlight] = useState(null);
  const [highlightLoading, setHighlightLoading] = useState(false);
  // Crossfade background state
  const [bgA, setBgA] = useState('https://images.pexels.com/photos/27305293/pexels-photo-27305293.jpeg?auto=compress&cs=tinysrgb&w=1600');
  const [bgB, setBgB] = useState(null);
  const [showB, setShowB] = useState(false);

  const changeBg = (newImage) => {
    setShowB(prev => {
      if (prev) { setBgA(newImage); } else { setBgB(newImage); }
      return !prev;
    });
  };
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showUberLocationPicker, setShowUberLocationPicker] = useState(false);
  const [heroLocationObj, setHeroLocationObj] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showSurpriseMe, setShowSurpriseMe] = useState(false);
  const [feedMode, setFeedMode] = useState('discover'); // 'discover' | 'premium'
  const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Check promote subscription status
  useEffect(() => {
    if (!user) { setIsPremiumUser(false); return; }
    const token = localStorage.getItem('fq_auth_token');
    fetch(`${API_URL}/api/venues/subscription/status`, {
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setIsPremiumUser(d.can_post || false); })
      .catch(() => {});
  }, [user]);

  const handlePromoteCheckout = async () => {
    if (!user) { setShowPremiumUpsell(false); setShowAuthModal(true); return; }
    setCheckoutLoading(true);
    try {
      const token = localStorage.getItem('fq_auth_token');
      const res = await fetch(`${API_URL}/api/venues/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin, tier: 'basic' }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      }
    } catch {} finally { setCheckoutLoading(false); }
  };

  // AI Daily Quest
  const [dailyQuest, setDailyQuest] = useState(null);
  const [questLoading, setQuestLoading] = useState(false);

  // Unified FoodCard Modal
  const [showFoodCardModal, setShowFoodCardModal] = useState(false);
  const [foodCardModalContext, setFoodCardModalContext] = useState(null);
  const [foodCardModalMode, setFoodCardModalMode] = useState('post');

  // Listen for global "open-foodcard-modal" event (from bottom nav, etc.)
  useEffect(() => {
    const handler = (e) => {
      setFoodCardModalMode(e.detail?.mode || 'post');
      setFoodCardModalContext(e.detail?.questContext || null);
      setShowFoodCardModal(true);
    };
    window.addEventListener('open-foodcard-modal', handler);
    return () => window.removeEventListener('open-foodcard-modal', handler);
  }, []);

  useEffect(() => {
    const city = userLocation?.city || locationString?.split(',')[0] || '';
    setQuestLoading(true);
    fetch(`${API_URL}/api/quests/daily?city=${encodeURIComponent(city)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setDailyQuest(d); })
      .catch(() => {})
      .finally(() => setQuestLoading(false));
  }, [locationString, userLocation]);

  // Headline animation
  const [fadeIn, setFadeIn] = useState(true);
  const [userPicked, setUserPicked] = useState(false);

  // AI-generated home feed
  const [feed, setFeed] = useState({ restaurants: [], deals: [], events: [] });
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState(null);

  // AI-generated local Foodie Feed posts
  const [foodiePosts, setFoodiePosts] = useState([]);
  const [foodieLoading, setFoodieLoading] = useState(false);
  const [foodieError, setFoodieError] = useState(false);

  // Activity summary for enhanced personalization (fetched when user logs in)
  const [activitySummary, setActivitySummary] = useState(null);

  useEffect(() => {
    if (!user) { setActivitySummary(null); return; }
    fetch(`${API_URL}/api/activity/summary`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setActivitySummary(data); })
      .catch(() => {});
  }, [user]);

  const userPreferences = (() => {
    const foodlistCuisines = [...new Set(foodlistItems.map(i => i.cuisine).filter(Boolean))];
    const activityCuisines = activitySummary?.top_cuisines || [];
    const allCuisines = [...new Set([...foodlistCuisines, ...activityCuisines])];
    return {
      cuisines: allCuisines.slice(0, 5),
      vibes: activitySummary?.top_vibes || [],
      vibe: selectedVibe !== 'all' ? selectedVibe : (activitySummary?.top_vibes?.[0] || null),
      dietary: selectedDietary !== 'all' ? selectedDietary : null,
    };
  })();

  // ── localStorage cache helpers ───────────────────────────────────────────
  const FEED_KEY    = (loc) => `home_feed_${(loc || 'world').split(',')[0].trim().toLowerCase().replace(/\s/g,'_')}`;
  const FOODIE_KEY  = (loc) => `home_foodie_${(loc || 'world').split(',')[0].trim().toLowerCase().replace(/\s/g,'_')}`;
  const HIGHLIGHT_KEY = (loc) => `hero_highlight_${(loc || 'world').split(',')[0].trim().toLowerCase().replace(/\s/g,'_')}`;
  const HIGHLIGHT_DATE_KEY = (loc) => `hero_highlight_date_${(loc || 'world').split(',')[0].trim().toLowerCase().replace(/\s/g,'_')}`;

  // Load from localStorage immediately on mount
  useEffect(() => {
    if (!locationString) return;
    try {
      const cached = localStorage.getItem(FEED_KEY(locationString));
      if (cached) { const d = JSON.parse(cached); setFeed(d); }
    } catch { /* ignore */ }
    try {
      const cached = localStorage.getItem(FOODIE_KEY(locationString));
      if (cached) { const d = JSON.parse(cached); setFoodiePosts(d); }
    } catch { /* ignore */ }
  }, [locationString]); // eslint-disable-line

  // Fetch AI feed — only if no localStorage cache
  const fetchFeed = async (loc, force = false) => {
    const locationToUse = loc || locationString;
    if (!locationToUse) return;
    if (!force && localStorage.getItem(FEED_KEY(locationToUse))) return; // skip: cache exists
    setFeedLoading(true);
    setFeedError(null);
    try {
      const res = await fetch(`${API_URL}/api/home/feed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location: locationToUse, user_preferences: userPreferences }),
      });
      if (!res.ok) throw new Error('Failed to load feed');
      const data = await res.json();
      setFeed(data);
      try { localStorage.setItem(FEED_KEY(locationToUse), JSON.stringify(data)); } catch { /* full */ }
    } catch (err) {
      setFeedError(err.message);
    } finally {
      setFeedLoading(false);
    }
  };

  // Fetch Foodie Feed — only if no localStorage cache
  const fetchFoodieFeed = async (loc, force = false) => {
    const locationToUse = loc || locationString;
    if (!locationToUse) return;
    if (!force && localStorage.getItem(FOODIE_KEY(locationToUse))) return; // skip: cache exists
    setFoodieLoading(true);
    setFoodieError(false);
    try {
      const res = await fetch(`${API_URL}/api/home/foodie-feed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location: locationToUse }),
      });
      if (res.ok) {
        const data = await res.json();
        setFoodiePosts(data.posts || []);
        try { localStorage.setItem(FOODIE_KEY(locationToUse), JSON.stringify(data.posts || [])); } catch { /* full */ }
      } else { setFoodieError(true); }
    } catch { setFoodieError(true); } finally { setFoodieLoading(false); }
  };

  const fetchDailyHighlight = async (loc, force = false) => {
    const locationToUse = loc || locationString || 'world';
    const todayKey = new Date().toISOString().slice(0, 10);
    const cacheKey = HIGHLIGHT_KEY(locationToUse);
    const dateKey = HIGHLIGHT_DATE_KEY(locationToUse);

    if (!force) {
      try {
        const cached = localStorage.getItem(cacheKey);
        const cachedDate = localStorage.getItem(dateKey);
        if (cached && cachedDate === todayKey) {
          setDailyHighlight(JSON.parse(cached));
          return;
        }
      } catch { /* ignore */ }
    }

    setHighlightLoading(true);
    try {
      const params = new URLSearchParams();
      if (loc || locationString) params.append('city', loc || locationString);
      const url = params.toString()
        ? `${API_URL}/api/posts/highlight-of-day?${params.toString()}`
        : `${API_URL}/api/posts/highlight-of-day`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to load highlight');
      const data = await res.json();
      if (data?.image_url) {
        setDailyHighlight(data);
        try {
          localStorage.setItem(cacheKey, JSON.stringify(data));
          localStorage.setItem(dateKey, todayKey);
        } catch { /* full */ }
      }
    } catch { /* ignore */ }
    finally { setHighlightLoading(false); }
  };

  useEffect(() => {
    if (locationString) {
      fetchFeed(locationString);
      fetchFoodieFeed(locationString);
    }
  }, [locationString]); // eslint-disable-line

  useEffect(() => {
    fetchDailyHighlight(locationString);
  }, [locationString]); // eslint-disable-line

  useEffect(() => {
    if (!dailyHighlight?.image_url) return;
    if (userPicked || searchType !== 'dine-out') return;
    changeBg(dailyHighlight.image_url);
  }, [dailyHighlight?.image_url, userPicked, searchType]);

  useEffect(() => {
    if (userLocation && !heroLocationObj) {
      setHeroLocationObj(userLocation);
    }
  }, [userLocation]); // eslint-disable-line

  // Premium background images for each comfort type
  const staticBgImages = {
    'dine-out': 'https://images.pexels.com/photos/27305293/pexels-photo-27305293.jpeg?auto=compress&cs=tinysrgb&w=1600',
    'dine-in': 'https://images.pexels.com/photos/13432287/pexels-photo-13432287.jpeg?auto=compress&cs=tinysrgb&w=1600',
    'cook-at-home': 'https://images.pexels.com/photos/4113886/pexels-photo-4113886.jpeg?auto=compress&cs=tinysrgb&w=1600',
    'surprise-me': 'https://images.pexels.com/photos/18660106/pexels-photo-18660106.jpeg?auto=compress&cs=tinysrgb&w=1600',
  };

  const getBgImage = (type) => {
    if (dailyHighlight?.image_url && type === 'dine-out') {
      return dailyHighlight.image_url;
    }
    return staticBgImages[type] || staticBgImages['dine-out'];
  };

  const CYCLE_ORDER = ['dine-out', 'dine-in', 'cook-at-home'];
  useEffect(() => {
    if (userPicked) return;
    const timer = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setSearchType(prev => {
          const idx = CYCLE_ORDER.indexOf(prev);
          const next = CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length];
          changeBg(getBgImage(next));
          return next;
        });
        setFadeIn(true);
      }, 420);
    }, 3600);
    return () => clearInterval(timer);
  }, [userPicked]); // eslint-disable-line

  const cityLabel = userLocation?.city || 'Near You';

  const comfortMeta = {
    'dine-out':     { emoji: '🍽️', label: 'Dine Out',     desc: 'Book a table at top restaurants',     headline: 'Find Your Table',    sub: `in ${cityLabel}` },
    'dine-in':      { emoji: '🏠', label: 'Order In',      desc: 'Order delivery to your door',         headline: 'Order Delivered',    sub: 'to your door' },
    'cook-at-home': { emoji: '👨‍🍳', label: 'Prepare It', desc: 'Get recipes & step-by-step guides',   headline: 'Cook Like a Chef',   sub: 'at home tonight' },
  };

  const handleComfortChange = (type) => {
    setFadeIn(false);
    setTimeout(() => {
      setSearchType(type);
      changeBg(getBgImage(type));
      setFadeIn(true);
    }, 200);
    setUserPicked(true);
    setOpenDropdown(null);
  };

  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const vibes = ['Energizing', 'Fresh', 'Hearty', 'Comfort', 'Celebrate', 'Indulgent'];
  const cuisineTypes = ['Italian', 'Chinese', 'Japanese', 'Indian', 'Mexican', 'Thai', 'American', 'French', 'Mediterranean', 'Korean'];
  const dietaryReqs = ['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Halal', 'Kosher', 'Keto', 'Paleo'];

  const handleSearch = () => {
    if (heroLocationObj && heroLocationObj.displayName !== locationString) {
      setGeocodedLocation(heroLocationObj);
    }
    const searchLocation = heroLocationObj?.displayName || locationString;
    track('search', { comfort: searchType, vibe: selectedVibe !== 'all' ? selectedVibe : null, cuisine: selectedCuisine !== 'all' ? selectedCuisine : null, location: searchLocation });
    navigate(`/recommendations?comfort=${searchType}`);
  };

  const meta = comfortMeta[searchType] || comfortMeta['dine-out'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] to-white pb-20 md:pb-8">
      {/* ── Hero – Original FoodQuest style ── */}
      <div className="relative" style={{ height: 'clamp(540px, 72vh, 720px)' }}>
        {/* Crossfade backgrounds */}
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgA})`, opacity: showB ? 0 : 1, transition: 'opacity 0.85s ease-in-out', zIndex: 0 }} />
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: bgB ? `url(${bgB})` : 'none', opacity: showB ? 1 : 0, transition: 'opacity 0.85s ease-in-out', zIndex: 0 }} />

        {/* Content */}
        <div className="relative h-full flex flex-col justify-end max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-16">
          {/* Hero text – left-aligned with orange accent bar */}
          <div className="mb-4 md:mb-8 w-full md:w-fit max-w-full">
            <div className="flex items-start gap-3 md:gap-5 w-full">
              <div className="w-[3px] rounded-full bg-[#FF6B35] self-stretch min-h-[60px] md:min-h-[80px] flex-shrink-0" />
              <div className="flex-1 min-w-0 flex flex-col gap-3 md:gap-4">
                <h1 style={{ transition: 'opacity 0.42s ease, transform 0.42s ease', opacity: fadeIn ? 1 : 0, transform: fadeIn ? 'translateY(0px)' : 'translateY(10px)', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }} className="leading-[1.1] tracking-tight">
                  <span className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-white block md:inline">{meta.headline}</span>
                  <span className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-semibold text-[#F4C430] block md:inline" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}> {meta.sub}</span>
                </h1>
                <p className="text-white text-xs md:text-sm font-light leading-relaxed hidden sm:block" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>Discover great food people love. Build your food journey.</p>
                <button onClick={() => navigate('/surprise-me')}
                  className="mt-2 group inline-flex items-center gap-2 bg-white/[0.12] border border-white/[0.25] hover:bg-white/[0.18] hover:border-[#F4C430]/50 backdrop-blur-sm rounded-full px-4 py-1.5 transition-all hover:scale-105"
                  data-testid="surprise-me-pill">
                  <span className="text-white/90 text-sm font-semibold group-hover:text-white transition-colors">Surprise Me —</span>
                  <span className="cta-shimmer text-[#F4C430] text-sm font-bold group-hover:text-[#FFD700] transition-colors">Let AI Suggest</span>
                  <Sparkles className="w-4 h-4 text-[#F4C430] cta-shimmer group-hover:text-[#FFD700]" />
                </button>
              </div>
            </div>
          </div>

          {/* Dark glass search bar */}
          <div className="w-full bg-black/45 backdrop-blur-md rounded-2xl p-3 md:p-4" data-testid="hero-search-bar">
            {/* Row 1: Comfort · Location */}
            <div className="flex items-center gap-2 md:gap-4 w-full">
              <div className="relative flex-shrink-0">
                <button data-testid="comfort-selector" onClick={() => toggleDropdown('comfort')} className="flex items-center gap-1.5 text-white font-semibold text-xs md:text-base hover:text-[#F4C430] transition-colors whitespace-nowrap">
                  <span className="text-base md:text-xl">{meta.emoji}</span>
                  <span>{meta.label}</span>
                  <ChevronDown className={`w-3 h-3 md:w-4 md:h-4 text-white/60 transition-transform ${openDropdown === 'comfort' ? 'rotate-180' : ''}`} />
                </button>
                {openDropdown === 'comfort' && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50 w-56 md:w-64">
                    {Object.entries(comfortMeta).filter(([key]) => key !== 'surprise-me').map(([key, v]) => (
                      <button key={key} onClick={() => handleComfortChange(key)} className={`w-full px-3 md:px-4 py-2.5 md:py-3 text-left flex items-center gap-3 hover:bg-[#FFF5F0] transition-colors border-b border-gray-50 last:border-0 ${searchType === key ? 'bg-[#FFF5F0]' : ''}`}>
                        <span className="text-lg flex-shrink-0">{v.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold leading-tight ${searchType === key ? 'text-[#FF6B35]' : 'text-gray-900'}`}>{v.label}</p>
                          <p className="text-xs text-gray-400 mt-0.5 leading-tight">{v.desc}</p>
                        </div>
                        {searchType === key && <span className="w-2 h-2 bg-[#FF6B35] rounded-full flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-white/30 text-sm md:text-lg flex-shrink-0">·</span>
              <button onClick={() => setShowUberLocationPicker(true)} className="flex-1 flex items-center gap-1.5 min-w-0 hover:text-[#F4C430] transition-colors cursor-pointer" data-testid="hero-location-btn">
                <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4 text-white/60 flex-shrink-0" />
                <span className="text-white/90 text-xs md:text-sm font-semibold truncate">{locationString || 'Set location'}</span>
              </button>
            </div>

            <div className="w-full h-px bg-white/25" />

            {/* Row 2: Refine + CTAs */}
            <div className="flex items-center justify-between py-3 md:py-4 w-full gap-3">
              <div className="flex items-center gap-1 md:gap-2 flex-wrap min-w-0">
                <span className="text-white/60 text-xs md:text-sm mr-1 font-light tracking-wide">Refine:</span>
                {/* Vibe */}
                <div className="relative">
                  <button onClick={() => toggleDropdown('vibe')} className={`flex items-center gap-1 text-xs md:text-sm font-semibold transition-all ${selectedVibe !== 'all' ? 'text-[#F4C430]' : 'text-white/80 hover:text-white'}`} data-testid="refine-vibe-btn">
                    {selectedVibe === 'all' ? 'Any Vibe' : vibes.find(v => v.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-') === selectedVibe) || 'Any Vibe'}
                    <ChevronDown className="w-3 h-3 opacity-70" />
                  </button>
                  {openDropdown === 'vibe' && (
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-56 overflow-y-auto z-50 w-40 md:w-44">
                      <button onClick={() => { setSelectedVibe('all'); setOpenDropdown(null); }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-[#FFF5F0] text-gray-900 font-medium border-b border-gray-100">Any Vibe</button>
                      {vibes.map(v => <button key={v} onClick={() => { setSelectedVibe(v.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')); setOpenDropdown(null); }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-[#FFF5F0] text-gray-700 border-b border-gray-50 last:border-0">{v}</button>)}
                    </div>
                  )}
                </div>
                <span className="text-white/30 text-xs">·</span>
                {/* Cuisine */}
                <div className="relative">
                  <button onClick={() => toggleDropdown('cuisine')} className={`flex items-center gap-1 text-xs md:text-sm font-semibold transition-all ${selectedCuisine !== 'all' ? 'text-[#F4C430]' : 'text-white/80 hover:text-white'}`} data-testid="refine-cuisine-btn">
                    {selectedCuisine === 'all' ? 'Any Cuisine' : cuisineTypes.find(c => c.toLowerCase() === selectedCuisine) || 'Any Cuisine'}
                    <ChevronDown className="w-3 h-3 opacity-70" />
                  </button>
                  {openDropdown === 'cuisine' && (
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-56 overflow-y-auto z-50 w-40 md:w-44">
                      <button onClick={() => { setSelectedCuisine('all'); setOpenDropdown(null); }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-[#FFF5F0] text-gray-900 font-medium border-b border-gray-100">Any Cuisine</button>
                      {cuisineTypes.map(c => <button key={c} onClick={() => { setSelectedCuisine(c.toLowerCase()); setOpenDropdown(null); }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-[#FFF5F0] text-gray-700 border-b border-gray-50 last:border-0">{c}</button>)}
                    </div>
                  )}
                </div>
                <span className="text-white/30 text-xs">·</span>
                {/* Dietary */}
                <div className="relative">
                  <button onClick={() => toggleDropdown('dietary')} className={`flex items-center gap-1 text-xs md:text-sm font-semibold transition-all ${selectedDietary !== 'all' ? 'text-[#F4C430]' : 'text-white/80 hover:text-white'}`} data-testid="refine-diet-btn">
                    {selectedDietary === 'all' ? 'Any Diet' : dietaryReqs.find(d => d.toLowerCase().replace(/-/g, '') === selectedDietary) || 'Any Diet'}
                    <ChevronDown className="w-3 h-3 opacity-70" />
                  </button>
                  {openDropdown === 'dietary' && (
                    <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-56 overflow-y-auto z-50 w-40 md:w-44">
                      <button onClick={() => { setSelectedDietary('all'); setOpenDropdown(null); }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-[#FFF5F0] text-gray-900 font-medium border-b border-gray-100">Any Diet</button>
                      {dietaryReqs.map(d => <button key={d} onClick={() => { setSelectedDietary(d.toLowerCase().replace(/-/g, '')); setOpenDropdown(null); }} className="w-full px-4 py-2.5 text-left text-sm hover:bg-[#FFF5F0] text-gray-700 border-b border-gray-50 last:border-0">{d}</button>)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Promote — outlined premium CTA */}
                <button data-testid="toggle-promote" onClick={() => {
                  if (isPremiumUser) { setFeedMode('premium'); }
                  else { setShowPremiumUpsell(true); }
                }}
                  className="h-10 px-5 md:px-7 border-2 border-[#F4C430]/60 text-[#F4C430] text-xs md:text-sm font-bold rounded-2xl flex items-center gap-2 hover:bg-[#F4C430]/10 hover:border-[#F4C430] active:scale-95 transition-all backdrop-blur-sm">
                  <Lock className="w-3.5 h-3.5" />Promote
                </button>
                {/* Discover — primary CTA */}
                <button data-testid="toggle-discover" onClick={() => { setFeedMode('discover'); handleSearch(); }}
                  className="h-10 px-5 md:px-7 bg-gradient-to-b from-[#F5C842] via-[#FF9248] to-[#FF7340] text-white text-xs md:text-sm font-bold rounded-2xl flex items-center gap-2 hover:opacity-95 active:scale-95 transition-all shadow-lg shadow-orange-500/20">
                  <Sparkles className="w-4 h-4" />Discover
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <SurpriseMeModal isOpen={showSurpriseMe} onClose={() => setShowSurpriseMe(false)} />
      {showLocationModal && <LocationPickerModal onClose={() => setShowLocationModal(false)} />}
      {showUberLocationPicker && <UberStyleLocationPicker onClose={() => setShowUberLocationPicker(false)} />}

      {/* Premium Upsell Modal */}
      {showPremiumUpsell && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowPremiumUpsell(false)} />
          <div className="relative bg-white w-full max-w-sm rounded-t-[28px] md:rounded-[28px] shadow-2xl p-6 md:p-8 animate-in slide-in-from-bottom duration-300" data-testid="premium-upsell-modal">
            <button onClick={() => setShowPremiumUpsell(false)} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"><XIcon className="w-5 h-5 text-gray-400" /></button>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#F4C430] to-[#FF6B35] rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-200">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Unlock Promote</h2>
              <p className="text-sm text-gray-500 mt-2">Boost your FoodCards to reach more foodies — $10/mo unlimited</p>
            </div>
            <div className="space-y-3 mb-6">
              {['"Promote this" checkbox on every post', 'Promoted badge — higher feed rank, wider reach', 'Unlimited promoted posts per month', 'Works for home cooks, food trucks & restaurants'].map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-gray-600">
                  <div className="w-5 h-5 bg-emerald-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-3 h-3 text-emerald-500" />
                  </div>
                  {f}
                </div>
              ))}
            </div>
            <button onClick={handlePromoteCheckout} disabled={checkoutLoading} className="w-full py-3.5 bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white font-bold text-sm rounded-2xl hover:opacity-95 active:scale-[0.98] transition-all shadow-lg disabled:opacity-50" data-testid="premium-subscribe-btn">
              {checkoutLoading ? 'Opening checkout...' : 'Unlock for $10/mo — Boost Your Posts'}
            </button>
            <button onClick={() => setShowPremiumUpsell(false)} className="w-full mt-2 py-2.5 text-gray-400 text-sm font-medium hover:text-gray-600 transition-colors">
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Sticky Right-Side Nav */}
      <StickyRightNav />
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}

      {/* ══════════════════════════════════════════════════════════════
          NETFLIX-STYLE CAROUSEL ROWS
          ══════════════════════════════════════════════════════════════ */}
      <div className="space-y-8 py-6 md:py-8">

        {/* Location badge — shows where discoveries are for */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {locationString ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <MapPin className="w-4 h-4 text-[#FF6B35]" />
                <span>Discovering in <span className="font-semibold text-gray-900">{locationString}</span></span>
                <button onClick={() => setShowUberLocationPicker(true)} className="text-[#FF6B35] text-xs font-medium hover:underline" data-testid="change-location-btn">Change</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowUberLocationPicker(true)}
              className="w-full py-3 bg-gradient-to-r from-[#FFF5F0] to-white border border-[#FF6B35]/20 rounded-2xl flex items-center justify-center gap-2 text-sm font-medium text-[#FF6B35] hover:bg-[#FFF5F0] transition-colors"
              data-testid="set-location-prompt">
              <MapPin className="w-4 h-4" />Set your location for personalized discoveries
            </button>
          )}
        </div>

        {/* ── 1. Foodie Feed ── */}
        <section id="foodie-feed" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">Foodie Feed</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {locationString ? `What foodies are eating in ${locationString.split(',')[0]}` : 'What foodies are eating now'}
              </p>
            </div>
            <Link to="/foodie" className="text-sm text-[#FF6B35] font-semibold flex items-center gap-1 hover:text-[#E85D04]">
              See All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
            {foodieLoading ? (
              [1,2,3,4].map(i => <div key={i} className="snap-start shrink-0 w-[260px] md:w-[300px] h-[280px] bg-gray-100 rounded-[20px] animate-pulse" />)
            ) : (foodiePosts.length > 0 ? foodiePosts : []).slice(0, 8).map((post, i) => (
              <div key={post.id || i} className="snap-start shrink-0 w-[260px] md:w-[300px]">
                <div className="bg-white rounded-[20px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] transition-all">
                  <Link to="/foodie" className="block group">
                    <div className="relative h-[170px] overflow-hidden">
                      <img src={post.image} alt={post.caption} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" onError={(e)=>{e.target.src=FALLBACK_IMG;}} />
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent h-16" />
                      {post.cuisine && <span className="absolute bottom-2 left-2 bg-white/20 backdrop-blur-md text-white text-[10px] font-medium px-2 py-0.5 rounded-full">{post.cuisine}</span>}
                      {feedMode === 'premium' && <span className="absolute top-2 right-2 bg-[#F4C430] text-gray-900 text-[9px] font-bold px-2 py-0.5 rounded-full">Promoted</span>}
                    </div>
                    <div className="px-3 pt-2.5 pb-1">
                      <p className="text-xs font-bold text-gray-900 truncate">{post.caption?.split(/[.!?,]/)[0] || 'Delicious Dish'}</p>
                      {post.restaurantName && <p className="text-[11px] font-medium text-gray-600 truncate">{post.restaurantName}</p>}
                      {post.locationArea && <p className="text-[10px] text-gray-400 truncate flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5 flex-shrink-0" />{post.locationArea}</p>}
                    </div>
                  </Link>
                  <EngagementBarCompact itemName={post.caption || 'Food'} itemId={`feed-${post.id || i}`} foodlistCount={post.likes || 0} foodcardCount={post.comments || 0} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── 2. Near You / Trending Restaurants ── */}
        {locationString && (
          <section id="near-you" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  {feedMode === 'premium' ? 'Promoted Spots' : `Near You in ${locationString.split(',')[0]}`}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {feedMode === 'premium' ? 'Featured restaurants & premium picks' : 'Trending restaurants nearby'}
                </p>
              </div>
              <Link to="/recommendations?comfort=dine-out" className="text-sm text-[#FF6B35] font-semibold flex items-center gap-1 hover:text-[#E85D04]">
                See All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
              {feedLoading ? (
                [1,2,3,4].map(i => <div key={i} className="snap-start shrink-0 w-[280px] md:w-[320px] h-[260px] bg-gray-100 rounded-[20px] animate-pulse" />)
              ) : feed.restaurants.map((r, i) => (
                <div key={r.id || i} className="snap-start shrink-0 w-[280px] md:w-[320px]">
                  <div className="bg-white rounded-[20px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] transition-all">
                    <Link to="/recommendations?comfort=dine-out" className="block group">
                      <div className="relative h-[170px] overflow-hidden">
                        <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" onError={(e)=>{e.target.src=FALLBACK_IMG;}} />
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent h-20" />
                        <div className="absolute bottom-2.5 left-2.5 right-2.5"><p className="text-white font-bold text-sm leading-tight truncate">{r.name}</p></div>
                        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 bg-black/40 backdrop-blur-md px-2 py-1 rounded-full">
                          <Star className="w-3 h-3 fill-[#F4C430] text-[#F4C430]" />
                          <span className="text-white text-xs font-semibold">{r.rating}</span>
                          <span className="text-white/50 text-xs">·</span>
                          <span className="text-white text-xs">{r.priceRange}</span>
                        </div>
                        {r.openNow && <span className="absolute top-2.5 right-2.5 bg-emerald-500/90 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">Open</span>}
                        {feedMode === 'premium' && <span className="absolute bottom-2.5 right-2.5 bg-[#F4C430] text-gray-900 text-[9px] font-bold px-2 py-0.5 rounded-full">Featured</span>}
                      </div>
                      <div className="px-3 pt-2 pb-0.5">
                        <p className="text-xs font-bold text-gray-900 truncate">{r.topDish || r.cuisine + ' Speciality'}</p>
                        <p className="text-[11px] font-medium text-gray-600 truncate">{r.name}</p>
                        <p className="text-[10px] text-gray-400 truncate flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5 flex-shrink-0" />{(r.address || r.description || '').split(',')[0]}</p>
                      </div>
                    </Link>
                    <EngagementBarCompact itemName={r.name} itemId={`resto-${r.id || i}`} rating={r.rating} foodlistCount={Math.floor(Math.random() * 30) + 5} foodcardCount={Math.floor(Math.random() * 50) + 10} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── 3. Today's Deals ── */}
        {locationString && feed.deals.length > 0 && (
          <section id="deals" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  {feedMode === 'premium' ? 'Exclusive Deals' : "Today's Deals"}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {feedMode === 'premium' ? 'Premium member exclusives' : `AI-curated for ${locationString.split(',')[0]}`}
                </p>
              </div>
              <Link to="/deals" className="text-sm text-[#FF6B35] font-semibold flex items-center gap-1 hover:text-[#E85D04]">
                See All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
              {feed.deals.map((deal, i) => (
                <div key={deal.id || i} className="snap-start shrink-0 w-[300px] md:w-[340px]">
                  <div className="bg-white rounded-[20px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] transition-all">
                    <div className="relative h-[150px] overflow-hidden">
                      <img src={deal.image} alt={deal.title} className="w-full h-full object-cover" onError={(e)=>{e.target.src=FALLBACK_IMG;}} />
                      <Badge className="absolute top-2.5 left-2.5 bg-red-500 text-white text-xs px-2.5 py-0.5">{deal.discount}% OFF</Badge>
                      {feedMode === 'premium' && <span className="absolute top-2.5 right-2.5 bg-[#F4C430] text-gray-900 text-[9px] font-bold px-2 py-0.5 rounded-full">Exclusive</span>}
                    </div>
                    <div className="px-3 pt-2 pb-0.5">
                      <p className="text-xs font-bold text-gray-900 truncate">{deal.title}</p>
                      <p className="text-[11px] font-medium text-gray-600 truncate">{deal.restaurantName}</p>
                      {deal.location && <p className="text-[10px] text-gray-400 truncate flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5 flex-shrink-0" />{(deal.location || '').split(',')[0]}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-base font-black text-[#FF6B35]">${deal.discountPrice}</span>
                        <span className="text-xs text-gray-400 line-through">${deal.originalPrice}</span>
                      </div>
                    </div>
                    <EngagementBarCompact itemName={deal.title} itemId={`deal-${deal.id || i}`} foodlistCount={deal.purchased || 0} foodcardCount={Math.floor(Math.random() * 20) + 3} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── 4. AI Daily / Quests ── */}
        <section id="ai-quests" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">AI Daily Quests</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {feedMode === 'premium' ? 'Sponsored quest tie-ins' : 'Daily food challenges & AI picks'}
              </p>
            </div>
            <Link to="/surprise-me" className="text-sm text-[#FF6B35] font-semibold flex items-center gap-1 hover:text-[#E85D04]">
              See All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
            {/* Today's Quest — real Gemini-generated */}
            {dailyQuest && (
              <div className="snap-start shrink-0 w-[300px] md:w-[340px]">
                <QuestCard quest={dailyQuest} onResponded={() => {}} onVerifyWithModal={(ctx) => { setFoodCardModalContext(ctx); setFoodCardModalMode('quest_verify'); setShowFoodCardModal(true); }} />
              </div>
            )}
            {questLoading && !dailyQuest && (
              <div className="snap-start shrink-0 w-[300px] md:w-[340px] h-[220px] bg-gray-100 rounded-[20px] animate-pulse" />
            )}
            {/* Static fallback quests for variety */}
            {[
              { quest_id: 'static-1', title: 'Hidden Ramen Spot', blurb: 'Discover the best tonkotsu near you — rich, creamy, unforgettable. Upload a photo if you find it!', tag: 'AI Pick', disclosure: 'AI-suggested idea only – not representative of any real dish.' },
              { quest_id: 'static-2', title: 'Vegan Brunch Challenge', blurb: 'Find the best plant-based brunch in your area. Can you verify it? Photo proof earns a badge!', tag: 'Challenge', disclosure: 'AI-suggested idea only – not representative of any real dish.' },
            ].map((q) => (
              <div key={q.quest_id} className="snap-start shrink-0 w-[300px] md:w-[340px]">
                <QuestCard quest={q} onResponded={() => {}} onVerifyWithModal={(ctx) => { setFoodCardModalContext(ctx); setFoodCardModalMode('quest_verify'); setShowFoodCardModal(true); }} />
              </div>
            ))}
          </div>
        </section>

        {/* ── 5. Events & Festivals ── */}
        {locationString && feed.events.length > 0 && (
          <section id="events" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  {feedMode === 'premium' ? 'Premium Events' : 'This Week in ' + locationString.split(',')[0]}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">Festivals, classes & tastings</p>
              </div>
              <Link to="/events" className="text-sm text-[#FF6B35] font-semibold flex items-center gap-1 hover:text-[#E85D04]">
                See All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
              {feed.events.map((event, i) => (
                <div key={event.id || i} className="snap-start shrink-0 w-[300px] md:w-[340px]">
                  <div className="bg-white rounded-[20px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)] transition-all">
                    <div className="relative h-[150px] overflow-hidden">
                      <img src={event.image} alt={event.title} className="w-full h-full object-cover" onError={(e)=>{e.target.src=FALLBACK_IMG;}} />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <Badge className="absolute top-2.5 right-2.5 bg-[#FF6B35] text-white text-[10px]">{event.category}</Badge>
                      <div className="absolute bottom-2.5 left-2.5 right-2.5"><h3 className="text-white font-bold text-sm leading-tight truncate">{event.title}</h3></div>
                    </div>
                    <div className="px-3 pt-2 pb-0.5">
                      <p className="text-xs font-bold text-gray-900 truncate">{event.title}</p>
                      {event.venue && <p className="text-[11px] font-medium text-gray-600 truncate">{event.venue}</p>}
                      <p className="text-[10px] text-gray-400 truncate flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5 flex-shrink-0" />{(event.location || '').split(',')[0]}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-gray-500 flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5 text-[#FF6B35]" />{event.date ? new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                        <span className="text-xs font-bold text-[#FF6B35]">{event.price === 0 ? 'Free' : `$${event.price}`}</span>
                      </div>
                    </div>
                    <EngagementBarCompact itemName={event.title} itemId={`event-${event.id || i}`} foodlistCount={event.attendees || 0} foodcardCount={Math.floor(Math.random() * 15) + 2} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── 6. Friends' Favorites (optional social row) ── */}
        {user && (
          <section id="friends" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Friends' Favorites</h2>
                <p className="text-sm text-gray-500 mt-0.5">What people you follow are saving</p>
              </div>
              <Link to="/foodie?section=following" className="text-sm text-[#FF6B35] font-semibold flex items-center gap-1 hover:text-[#E85D04]">
                See All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
              {foodlistItems.slice(0, 6).map((item, i) => (
                <div key={i} className="snap-start shrink-0 w-[220px]">
                  <div className="bg-white rounded-[20px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-md transition-all p-4 text-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#F4C430] to-[#FF6B35] mx-auto mb-3 flex items-center justify-center text-white text-lg font-bold">
                      {(item.name || 'F')[0]}
                    </div>
                    <p className="text-sm font-bold text-gray-900 truncate">{item.name}</p>
                    <p className="text-[11px] text-gray-400 truncate mt-0.5">{item.restaurant || item.cuisine || ''}</p>
                  </div>
                </div>
              ))}
              {foodlistItems.length === 0 && (
                <div className="snap-start shrink-0 w-full text-center py-8">
                  <p className="text-sm text-gray-400">Follow foodies to see their favorites here</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ── 7. More to Explore / Hidden Gems ── */}
        <section id="hidden-gems" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">Hidden Gems</h2>
              <p className="text-sm text-gray-500 mt-0.5">AI-curated niche picks you might love</p>
            </div>
            <Link to="/recommendations?comfort=dine-out" className="text-sm text-[#FF6B35] font-semibold flex items-center gap-1 hover:text-[#E85D04]">
              See All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
            {['Vegan Gems', 'Late-Night Spots', 'Hole-in-the-Wall', 'Dessert Havens', 'Brunch Spots'].map((cat, i) => (
              <Link to={`/recommendations?comfort=dine-out&q=${encodeURIComponent(cat)}`} key={i} className="snap-start shrink-0 w-[200px] md:w-[220px]">
                <div className="relative h-[140px] rounded-[20px] overflow-hidden group cursor-pointer">
                  <div className={`absolute inset-0 bg-gradient-to-br ${['from-rose-500 to-pink-600','from-indigo-500 to-purple-600','from-emerald-500 to-teal-600','from-amber-400 to-orange-500','from-sky-400 to-blue-500'][i]} group-hover:scale-105 transition-transform duration-500`} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
                    <h3 className="text-base font-bold text-center">{cat}</h3>
                    <p className="text-[10px] text-white/70 mt-1">Explore</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>

      {/* Unified FoodCard Creation Modal */}
      <UnifiedFoodCardModal
        isOpen={showFoodCardModal}
        onClose={() => { setShowFoodCardModal(false); setFoodCardModalContext(null); setFoodCardModalMode('post'); }}
        questContext={foodCardModalContext}
        mode={foodCardModalMode}
      />
    </div>
  );
};

export default Home;
